import { Link, useLocation } from "react-router-dom";
import { tr } from "../../i18n";

export interface DesktopNavItem {
  to: string;
  label: string;
  icon?: string;
  match: (pathname: string) => boolean;
}

interface DesktopNavProps {
  /** Voci a sinistra (vista squadra: classifica, calendario, rosa, formazione) */
  leftItems: DesktopNavItem[];
  /** Voci a destra (strumenti: regolamento, budget, mercato, notifiche) */
  rightItems: DesktopNavItem[];
  /** Mostra icona Home come primissima voce a sinistra */
  withHomeIcon?: boolean;
}

/**
 * Barra nav orizzontale desktop a 2 raggruppamenti separati da slash al centro.
 *
 * Layout:
 *   [⌂ home] [left items]                /                [right items]
 *
 * Active state oro + underline. Hover lighter purple.
 */
export function DesktopNav({ leftItems, rightItems, withHomeIcon = true }: DesktopNavProps) {
  const { pathname } = useLocation();

  const renderItem = (item: DesktopNavItem) => {
    const active = item.match(pathname);
    return (
      <Link
        key={item.to + item.label}
        to={item.to}
        className={`px-4 py-3 text-sm font-display tracking-wide transition relative whitespace-nowrap ${
          active ? "text-accent" : "text-fg hover:text-accent-soft hover:bg-primary-soft/40"
        }`}
      >
        {item.icon && <span className="mr-1.5">{item.icon}</span>}
        {tr(item.label)}
        {active && (
          <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-accent rounded-full" />
        )}
      </Link>
    );
  };

  return (
    <nav className="bg-primary border-b border-primary-soft/30">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-2">
        {/* LEFT: home + voci squadra */}
        <div className="flex items-center">
          {withHomeIcon && (
            <Link
              to="/"
              className={`flex items-center justify-center w-12 h-12 hover:bg-primary-soft/40 transition ${
                pathname === "/" ? "text-accent" : "text-fg hover:text-accent-soft"
              }`}
              title={tr("Home")}
              aria-label={tr("Home")}
            >
              {/* Andrea 2026-06-15: icona casa piena (eredita il colore del
                  link), più chiara del vecchio glifo "⌂". */}
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M11.47 3.84a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 1-1.06 1.06l-.31-.31V19.5a1.5 1.5 0 0 1-1.5 1.5h-3a.75.75 0 0 1-.75-.75V15a.75.75 0 0 0-.75-.75h-1.5a.75.75 0 0 0-.75.75v5.25a.75.75 0 0 1-.75.75h-3A1.5 1.5 0 0 1 5.1 19.5v-6.22l-.31.31a.75.75 0 0 1-1.06-1.06l8.69-8.69Z" />
              </svg>
            </Link>
          )}
          {leftItems.map(renderItem)}
        </div>

        {/* DIVIDER */}
        <div className="font-display text-3xl text-accent select-none px-2" aria-hidden>
          /
        </div>

        {/* RIGHT: strumenti */}
        <div className="flex items-center">{rightItems.map(renderItem)}</div>
      </div>
    </nav>
  );
}
